package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.CaptureSource
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.InvalidateCapturesRequest
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.SlotInput
import dev.lingvo.shared.ShotExportKind
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.deleteScreenshotCapture
import dev.lingvo.site.api.invalidateScreenshotSlot
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.putScreenshotPlan
import dev.lingvo.site.api.screenshotCaptures
import dev.lingvo.site.api.screenshotCoverage
import dev.lingvo.site.api.screenshotPlan
import dev.lingvo.site.components.registerScreenshotCommands
import dev.lingvo.site.components.studio.ExportDownload
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.yield
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import kotlin.js.Date

/** Stacks the single primary action at phone widths while retaining full-size controls. */
val ShotPlanHeaderStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(Space.SM).styleModifier { property("display", "flex"); property("flex-direction", "column"); property("align-items", "stretch"); property("min-width", "0") } }
    Breakpoint.MD { Modifier.styleModifier { property("flex-direction", "row"); property("align-items", "end"); property("flex-wrap", "wrap") } }
}

/** The drawer gets a dedicated desktop column without forcing the coverage matrix wider than the page. */
val ShotPlanBodyStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(Space.LG).styleModifier { property("display", "flex"); property("align-items", "start"); property("min-width", "0") } }
}

private data class ShotPlanData(val project: ProjectDetailDto, val plan: ScreenshotPlanDto, val coverage: CoverageDto?, val captures: List<CaptureDto>, val partial: String? = null, val setup: ShotSetupFacts = ShotSetupFacts(), val storeImportAvailable: Boolean = false)

/** Reads under the same verified project boundary as Stores and retires a stale route's response. */
@Page("/app/projects/{slug}/screenshots")
@Layout(".components.layouts.AppLayout")
@Composable
fun ScreenshotsPlanPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    var params by remember(slug) { mutableStateOf(readScreenshotsAreaState(ctx)) }
    var reload by remember(subject) { mutableStateOf(0) }
    val platform = params.platform
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        val live = subject ?: awaitCancellation()
        requestProjectResource(live.action, slug, Session.organizations) { verified ->
            val plan = ApiClient.screenshotPlan(slug, platform)
            var importAvailable = false
            var bindingFailure: String? = null
            if (ProjectAuthority.resolve(verified.project, Session.organizations).canEditStructure) {
                try {
                    val apps = ApiClient.storeApps(slug)
                    if (apps.any { it.platform == platform }) {
                        val orgSlug = verified.project.orgSlug.ifBlank { Session.organizations.firstOrNull { it.id == verified.project.orgId }?.slug.orEmpty() }
                        importAvailable = storeImportAvailable(ProjectAuthority.resolve(verified.project, Session.organizations).role, platform, apps, ApiClient.storeConnections(orgSlug))
                    }
                } catch (cancelled: CancellationException) { throw cancelled }
                catch (failure: Throwable) { bindingFailure = shotRequestFailure(failure) }
            }
            try { ShotPlanData(verified.project, plan, ApiClient.screenshotCoverage(slug, platform).first,
                ApiClient.screenshotCaptures(slug, platform, superseded = true), partial = bindingFailure, setup = readShotSetupFacts(slug, platform), storeImportAvailable = importAvailable) }
            catch (cancelled: CancellationException) { throw cancelled }
            catch (failure: Throwable) { ShotPlanData(verified.project, plan, null, emptyList(), shotRequestFailure(failure)) }
        }
    }
    DisposableEffect(slug) {
        val pop: (Event) -> Unit = { params = readShotWindowState() }
        window.addEventListener("popstate", pop)
        onDispose { window.removeEventListener("popstate", pop) }
    }
    Column(Modifier.fillMaxWidth().gap(Space.LG).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
        PageHeader(t("app.projects.slug.screenshots.index.screenshots", "Screenshots"), t("app.projects.slug.screenshots.index.plan_the_shots_see_what_needs", "Plan the shots, see what needs a retake, and capture every language."))
        ScreenshotsAreaNav(slug, ScreenshotsTab.PLAN, params, { params = it })
        ScreenshotsOfflineState()
        loaded.refreshError?.let { error -> AsyncFailureState(error, subject = t("app.projects.slug.screenshots.index.the_screenshot_plan", "the screenshot plan"), onRetry = { reload++ }) }
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 5)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.index.the_screenshot_plan", "the screenshot plan"), onRetry = { reload++ })
            is Async.Success -> key(subject, platform) {
                ShotPlanContent(slug, state.value, params, { params = it }, { reload++ })
            }
        }
    }
}

@Composable
private fun ShotPlanContent(slug: String, initial: ShotPlanData, params: ScreenshotsAreaState, onParams: (ScreenshotsAreaState) -> Unit, onReload: () -> Unit) {
    var importStore by remember { mutableStateOf(false) }
    var usageEpoch by remember { mutableStateOf(0) }
    var plan by remember { mutableStateOf(initial.plan) }
    var draft by remember { mutableStateOf(plan.editableSlots()) }
    var coverage by remember { mutableStateOf(initial.coverage) }
    var captures by remember { mutableStateOf(initial.captures) }
    var partial by remember { mutableStateOf(initial.partial) }
    var focusFirst by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var saveFailure by remember { mutableStateOf<String?>(null) }
    var conflicted by remember { mutableStateOf(false) }
    var retry by remember { mutableStateOf(0) }
    var online by remember { mutableStateOf(window.navigator.onLine) }
    var deleteIndex by remember { mutableStateOf<Int?>(null) }
    var pendingDelete by remember { mutableStateOf<List<SlotInput>?>(null) }
    var deleteCapture by remember { mutableStateOf<CaptureDto?>(null) }
    var selection by remember { mutableStateOf<ShotUploadSelection?>(null) }
    var pickerCell by remember { mutableStateOf<ScreenshotsCell?>(null) }
    var pickerDevice by remember { mutableStateOf<DeviceClass?>(null) }
    var announcement by remember { mutableStateOf("") }
    var refusals by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var etag by remember { mutableStateOf<String?>(null) }
    var drawerPushed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val toasts = rememberToasts()
    val authority = ProjectAuthority.resolve(liveProjectRoute(slug)?.project, Session.organizations)
    val action = remember(slug, authority.role, Session.token) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    val claim = remember(slug, authority.role, Session.token) { ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.READ) }
    val readAuthorized = { claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true }
    val authorized = { readAuthorized() &&
        ProjectAuthority.resolve(liveProjectRoute(slug)?.project, Session.organizations).canWriteTranslations }
    var acceptedInitial by remember { mutableStateOf(initial.plan) }
    LaunchedEffect(initial) {
        draft = reloadedShotDraft(acceptedInitial, initial.plan, draft)
        acceptedInitial = initial.plan
        plan = initial.plan; coverage = initial.coverage; captures = initial.captures; partial = initial.partial
        saveFailure = null; conflicted = false
    }
    val latestCaptures by rememberUpdatedState(captures)
    suspend fun refreshCoverage() {
        val result = ApiClient.screenshotCoverage(slug, params.platform, etag)
        if (claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) != true) return
        etag = result.second
        result.first?.let { next ->
            val previousIds = coverage?.rows?.flatMap { it.cells }?.mapNotNull { it.captureId }?.toSet().orEmpty()
            val fresh = next.rows.flatMap { it.cells }.mapNotNull { it.captureId }.toSet() - previousIds
            coverage = next; partial = null
            if (fresh.isNotEmpty()) {
                val nextCaptures = ApiClient.screenshotCaptures(slug, params.platform, superseded = true)
                if (claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) != true) return
                captures = nextCaptures
                val count = captures.count { it.id in fresh && it.source == CaptureSource.AGENT }
                if (count > 0) {
                    val message = trf("screenshots.plan.agent_update", "{count} new captures from an agent.", "count" to count)
                    announcement = if (announcement == message) "$message " else message
                }
            }
        }
    }
    fun refreshLater() { scope.launch { runShotRefresh({ refreshCoverage() }, { if (readAuthorized()) partial = it }) } }
    val captureDates = remember(captures) { captures.associate { it.id to it.capturedAt } }
    DisposableEffect(slug, params.platform, claim) {
        val polling = ShotVisiblePoller(scope, {
            val last = latestCaptures.filter { it.source == CaptureSource.AGENT }.maxOfOrNull { Date.parse(it.capturedAt) }
            shotPollMillis(Date.now(), last)
        }, { refreshCoverage() }, { if (readAuthorized()) partial = it })
        fun update() { polling.setVisible(document.asDynamic().hidden != true) }
        val visibility: (Event) -> Unit = { update() }
        val connection: (Event) -> Unit = { online = window.navigator.onLine }
        document.addEventListener("visibilitychange", visibility)
        window.addEventListener("online", connection); window.addEventListener("offline", connection); update()
        onDispose { polling.close(); document.removeEventListener("visibilitychange", visibility)
            window.removeEventListener("online", connection); window.removeEventListener("offline", connection) }
    }
    LaunchedEffect(draft, online, saving, pendingDelete, retry) {
        if (!authority.canEditStructure || !online || saving || pendingDelete != null || conflicted || saveFailure != null || draft == plan.editableSlots()) return@LaunchedEffect
        delay(600)
        val sent = draft
        action.run(ScreenshotsPrivilege.PLAN, { saving = it }, {
            ApiClient.putScreenshotPlan(slug, params.platform, planWrite(plan, sent, prune = true), ifMatch = planEtag(plan.version))
        }, { failure ->
            saveFailure = shotRequestFailure(failure); conflicted = failure is ApiException && failure.status == 412
        }, { saved -> plan = saved.plan; saveFailure = null; refreshLater() })
    }
    LaunchedEffect(focusFirst, draft.isNotEmpty()) {
        if (focusFirst && draft.isNotEmpty()) {
            yield(); (document.querySelector("[data-shot-title] input") as? HTMLElement)?.focus(); focusFirst = false
        }
    }
    LaunchedEffect(pendingDelete) {
        if (pendingDelete != null) { delay(10_000); pendingDelete = null }
    }
    fun chooseFiles(files: List<File>) {
        if (saving || (plan.slots.isEmpty() && draft.isNotEmpty())) { partial = tr("app.projects.slug.screenshots.index.wait_for_the_shot_plan_to", "Wait for the shot plan to save, then choose the captures again."); return }
        val target = ShotUploadSelection(files, pickerCell, pickerDevice); pickerCell = null; pickerDevice = null
        if (plan.slots.isNotEmpty()) { selection = target; return }
        if (!authority.canEditStructure) { partial = tr("app.projects.slug.screenshots.index.ask_an_owner_or_admin_to", "Ask an owner or admin to plan the shots before uploading captures."); return }
        val paths = files.map { ((it.asDynamic().lingvoRelativePath as? String) ?: (it.asDynamic().webkitRelativePath as? String))?.takeIf { value -> value.isNotBlank() } ?: it.name }
        val inferred = importedShotPlan(paths, params.platform, coverage?.locales.orEmpty())
        if (inferred.isEmpty()) { partial = tr("app.projects.slug.screenshots.index.no_shot_plan_could_be_read", "No shot plan could be read from these filenames. Plan the shots first, then assign the captures."); return }
        action.run(ScreenshotsPrivilege.PLAN, { saving = it }, {
            ApiClient.putScreenshotPlan(slug, params.platform, planWrite(plan, inferred), ifMatch = planEtag(plan.version))
        }, { partial = shotRequestFailure(it) }, { saved ->
            plan = saved.plan; draft = plan.editableSlots()
            scope.launch { try { refreshCoverage(); if (authorized()) selection = target } catch (failure: Throwable) { if (readAuthorized()) partial = shotRequestFailure(failure) } }
        })
    }
    val upload = { pickerCell = null; pickerDevice = null; (document.getElementById("shot-capture-picker") as? HTMLElement)?.click(); Unit }
    registerScreenshotCommands(slug, params, authority.canWriteTranslations, upload)
    if (!authority.canWriteTranslations) ScreenshotsReadOnlyState()
    ScreenshotUsageMeter(slug, params.platform, usageEpoch + initial.plan.version.hashCode() + captures.hashCode())
    Row(ShotPlanHeaderStyle.toModifier()) {
        LSelect(params.device?.id.orEmpty(), listOf(LSelectOption("", t("app.projects.slug.screenshots.index.all_devices", "All devices"))) +
            DeviceClass.forPlatform(params.platform).map { LSelectOption(it.id, screenshotDeviceLabel(it)) }, { raw ->
                val updated = params.copy(device = DeviceClass.fromId(raw), cell = null); onParams(updated); UrlState.replace(updated.toParams(ScreenshotsTab.PLAN))
            }, label = t("app.projects.slug.screenshots.index.device", "Device"))
        if (draft.isNotEmpty()) LLinkButton(t("app.projects.slug.screenshots.index.open_design", "Open design"),
            screenshotsPath(slug, ScreenshotsTab.DESIGN, params) + "&mode=canvas")
        if (authority.canWriteTranslations && coverage != null && plan.slots.isNotEmpty()) LButton(t("app.projects.slug.screenshots.index.upload_captures", "Upload captures"), upload, variant = LButtonVariant.SECONDARY)
    }
    Div(Modifier.styleModifier { property("min-width", "0"); property("max-width", "100%") }.toAttrs { attr("data-plan-download", "") }) {
        ExportDownload(slug, params.platform, ShotExportKind.CAPTURES, params.device?.let { listOf(it) }.orEmpty(), enabled = captures.any { it.supersededAt == null })
    }
    if (importStore) StoreImport(slug, params.platform, { importStore = false }, { usageEpoch++; onReload() })
    ShotCapturePicker("shot-capture-picker", onFiles = ::chooseFiles)
    ShotCapturePicker("shot-folder-picker", folder = true, onFiles = ::chooseFiles)
    partial?.let { LAlert(it, variant = LAlertVariant.WARNING); LButton(t("app.projects.slug.screenshots.index.retry_coverage", "Retry coverage"), {
        scope.launch { try { refreshCoverage() } catch (failure: Throwable) { if (readAuthorized()) partial = shotRequestFailure(failure) } }
    }, variant = LButtonVariant.SECONDARY) }
    if (draft.isEmpty()) FirstRun(authority.canEditStructure, {
        draft = starterShotPlan(params.platform); focusFirst = true
    }, { (document.getElementById("shot-folder-picker") as? HTMLElement)?.click() }, initial.storeImportAvailable, { importStore = true })
    else {
        Div(ShotPlanBodyStyle.toModifier().toAttrs {}) {
            Column(Modifier.gap(Space.LG).styleModifier { property("flex", "1 1 0%"); property("width", "100%"); property("min-width", "0"); property("max-width", "100%"); property("align-items", "stretch"); property("box-sizing", "border-box") }) {
                coverage?.let { current -> ShotCoverageGrid(current, params.device, params.cell, { cell, device ->
                    val updated = params.copy(cell = cell); drawerPushed = true; onParams(updated); UrlState.push(updated.toParams(ScreenshotsTab.PLAN))
                }, { files, cell, device -> selection = ShotUploadSelection(files, cell, device) }, authority.canWriteTranslations, refusals, captureDates) }
                LBadge(if (saving) t("app.projects.slug.screenshots.index.saving", "Saving…") else if (saveFailure != null) t("app.projects.slug.screenshots.index.couldnt_save_retry", "Couldn't save. Retry") else
                    if (draft == plan.editableSlots()) t("app.projects.slug.screenshots.index.saved", "Saved") else t("app.projects.slug.screenshots.index.changes_in_this_tab", "Changes in this tab"))
                if (conflicted) LAlert(t("app.projects.slug.screenshots.index.the_plan_changed_in_another_tab", "The plan changed in another tab. Copy your changes before reloading; reloading replaces this tab's draft."), variant = LAlertVariant.WARNING)
                saveFailure?.let { LAlert(it, variant = LAlertVariant.WARNING); LButton(if (conflicted) t("app.projects.slug.screenshots.index.reload_the_server_plan", "Reload the server plan") else t("app.projects.slug.screenshots.index.retry_save", "Retry save"),
                    { if (conflicted) onReload() else { saveFailure = null; retry++ } }, variant = LButtonVariant.SECONDARY) }
                ShotSlotPlan(slug, plan, draft, coverage, authority.canEditStructure && pendingDelete == null,
                    { index, slot -> if (!conflicted) saveFailure = null; draft = draft.toMutableList().apply { set(index, slot) } }, { from, to ->
                        val before = draft; draft = moveShot(draft, from, to)
                        toasts.showWithAction(tr("app.projects.slug.screenshots.index.shot_order_changed", "Shot order changed."), tr("app.projects.slug.screenshots.index.undo", "Undo"), { draft = restoreShotOrder(draft, before.map { it.key }) })
                    }, { if (draft.size < shotPlanLimit(params.platform)) draft = duplicateShot(draft, it) }, { deleteIndex = it }, { slotId ->
                        action.run(ScreenshotsPrivilege.PLAN, {}, { ApiClient.invalidateScreenshotSlot(slug, params.platform, slotId,
                            InvalidateCapturesRequest(reason = tr("app.projects.slug.screenshots.index.queued_from_screenshot_plan", "Queued from screenshot plan"))) }, { saveFailure = shotRequestFailure(it) }, { refreshLater() })
                    })
            }
            val row = coverage?.rows?.firstOrNull { it.slotId == params.cell?.slotId }
            val locale = params.cell?.locale
            if (row != null && locale != null) {
                val cell = coverageGridCell(coverage!!, row.slotId, locale, params.device, refusals)
                val capture = drawerCaptureForCell(captures, row.slotId, cell.device, cell.capture?.reusesLocale ?: locale, cell.capture?.captureId)
                ShotCellDrawer(slug, params, row, cell, locale, capture, authority.canWriteTranslations, {
                    onParams(params.copy(cell = null)); if (drawerPushed) { drawerPushed = false; window.history.back() } else UrlState.replace(params.copy(cell = null).toParams(ScreenshotsTab.PLAN))
                }, { pickerCell = params.cell; pickerDevice = cell.device; (document.getElementById("shot-capture-picker") as? HTMLElement)?.click() }, {
                    action.run(ScreenshotsPrivilege.CAPTURE, {}, { ApiClient.invalidateScreenshotSlot(slug, params.platform, row.slotId,
                        InvalidateCapturesRequest(locales = listOf(locale), devices = cell.device?.let { listOf(it) }, reason = tr("app.projects.slug.screenshots.index.queued_from_coverage", "Queued from coverage"))) },
                        { saveFailure = shotRequestFailure(it) }, { refreshLater() })
                }, { deleteCapture = capture },
                    history = captureHistoryForCell(captures, row.slotId, cell.device, capture?.locale ?: cell.capture?.reusesLocale ?: locale),
                    onCaptureChanged = { updated ->
                        captures = captures.filterNot { it.id == updated.id || (it.slotId == updated.slotId && it.locale == updated.locale && it.deviceClass == updated.deviceClass && it.supersededAt == null) } + updated
                        etag = null; usageEpoch++; onReload()
                        scope.launch { yield(); (document.getElementById("shot-cell-drawer") as? HTMLElement)?.focus() }
                    })
            }
        }
    }
    initial.setup.failure?.let { LAlert(it, variant = LAlertVariant.WARNING) }
    ShotSetupChecklist(shotSetupSteps(draft.isNotEmpty(), coverage, initial.setup.hasDesign, initial.setup.hasDelivery))
    val deleting = deleteIndex?.let { draft.getOrNull(it) }
    LConfirmDialog(deleting != null, tf("screenshots.plan.delete_title", "Delete {slot}?", "slot" to deleting?.title.orEmpty()), t("app.projects.slug.screenshots.index.delete_slot", "Delete slot"), {
        val index = deleteIndex ?: return@LConfirmDialog
        val before = draft; val removed = draft[index]; pendingDelete = before; draft = draft.filterIndexed { i, _ -> i != index }; deleteIndex = null
        toasts.showWithAction(tr("app.projects.slug.screenshots.index.slot_will_be_deleted_in_10", "Deletion queued. Undo within 10 seconds. Leaving this screen cancels it."), tr("app.projects.slug.screenshots.index.undo", "Undo"),
            { if (pendingDelete != null) { draft = restoreDeletedShot(draft, removed, index); pendingDelete = null } }, durationMillis = 10_000)
    }, { deleteIndex = null }, consequence = shotDeleteConsequence(captures.count { capture -> capture.slotKey == deleting?.key }))
    LConfirmDialog(deleteCapture != null, t("app.projects.slug.screenshots.index.delete_this_capture", "Delete this capture?"), t("app.projects.slug.screenshots.index.delete_capture", "Delete capture"), {
        val capture = deleteCapture ?: return@LConfirmDialog
        action.run(ScreenshotsPrivilege.CAPTURE, {}, { ApiClient.deleteScreenshotCapture(slug, params.platform, capture.id) },
            { saveFailure = shotRequestFailure(it) }, { deleteCapture = null; captures = captures.filterNot { it.id == capture.id }; usageEpoch++; refreshLater() })
    }, { deleteCapture = null })
    val currentSelection = selection
    val currentCoverage = coverage
    if (currentSelection != null && currentCoverage != null) CaptureUpload(slug, plan, currentCoverage, captures, currentSelection,
        authorized, { selection = null }, { capture -> usageEpoch++; captures = mergeUploadedCaptures(captures, capture); refusals = refusals - "${capture.slotId}:${capture.locale}:${capture.deviceClass.id}"; refreshLater() },
        { target, reason -> refusals = refusals + ("${target.cell?.slotId}:${target.cell?.locale}:${target.device?.id}" to reason) })
    Div(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status"); attr("aria-live", "polite"); attr("aria-atomic", "true") }) { Text(announcement) }
    ToastHost(toasts)
}

private fun readShotWindowState(): ScreenshotsAreaState {
    val query = js("new URLSearchParams(window.location.search)")
    return ScreenshotsAreaState.parse(listOf("platform", "device", "cell").associateWith { query.get(it) as? String })
}
